import React, { useState } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';

const Withdraw: React.FC = () => {
  const colors = getThemeColors('dark');

  const [amount, setAmount] = useState('');
  const [withdrawMethod, setWithdrawMethod] = useState('mpesa');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);

  const availableBalance = 25000; // Mock balance

  const withdrawMethods = [
    { id: 'mpesa', name: 'M-Pesa', icon: '📱', placeholder: 'Enter M-Pesa number' },
    { id: 'bank', name: 'Bank Transfer', icon: '🏦', placeholder: 'Enter account number' },
  ];

  const handleWithdraw = async () => {
    const withdrawAmount = parseFloat(amount) || 0;

    if (!withdrawAmount || withdrawAmount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (withdrawAmount > availableBalance) {
      alert('Insufficient balance');
      return;
    }

    if (!phoneNumber.trim()) {
      alert('Please enter a phone number or account number');
      return;
    }

    try {
      setLoading(true);
      // Mock API call - in real app this would call actual API
      await new Promise(resolve => setTimeout(resolve, 2000));

      const methodName = withdrawMethod === 'mpesa' ? 'M-Pesa' : 'Bank Account';
      alert(`Withdrawal of KES ${withdrawAmount.toLocaleString()} initiated to ${methodName}`);
      setAmount('');
      setPhoneNumber('');
    } catch (error) {
      console.error('Withdrawal failed:', error);
      alert('Withdrawal failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: spacing.md,
    },
    header: {
      textAlign: 'center',
      marginBottom: spacing.xl,
    },
    title: {
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    subtitle: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    balanceCard: {
      maxWidth: '600px',
      margin: '0 auto',
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.lg,
      ...getShadowStyle('md'),
      textAlign: 'center',
    },
    balanceLabel: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      marginBottom: spacing.sm,
    },
    balanceAmount: {
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
      color: colors.success,
    },
    form: {
      maxWidth: '600px',
      margin: '0 auto',
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.xl,
      ...getShadowStyle('md'),
    },
    label: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      color: colors.text,
      marginBottom: spacing.sm,
      marginTop: spacing.md,
      display: 'block',
    },
    amountInput: {
      width: '100%',
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
      textAlign: 'center',
      border: `2px solid ${colors.border}`,
      backgroundColor: colors.surface,
      color: colors.text,
      outline: 'none',
      boxSizing: 'border-box',
    },
    methodSelector: {
      display: 'flex',
      gap: spacing.sm,
      marginBottom: spacing.md,
    },
    methodButton: {
      flex: 1,
      padding: spacing.md,
      borderRadius: borderRadius.lg,
      border: `2px solid ${colors.border}`,
      backgroundColor: colors.surface,
      color: colors.text,
      cursor: 'pointer',
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      transition: 'all 0.2s ease',
    },
    activeMethod: {
      borderColor: colors.primary,
      backgroundColor: colors.primary + '20',
      color: colors.primary,
    },
    input: {
      width: '100%',
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      fontSize: typography.fontSize.base,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
      color: colors.text,
      outline: 'none',
      boxSizing: 'border-box',
    },
    withdrawButton: {
      width: '100%',
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      backgroundColor: colors.error,
      border: 'none',
      color: colors.white,
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      cursor: 'pointer',
      marginTop: spacing.xl,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.sm,
      transition: 'all 0.2s ease',
    },
    loadingIcon: {
      animation: 'spin 1s linear infinite',
    },
  };

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Withdraw Money"
        subtitle="Withdraw funds from your wallet"
        showBackButton={true}
        showHomeButton={true}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          <div style={styles.balanceCard}>
            <div style={styles.balanceLabel}>Available Balance</div>
            <div style={styles.balanceAmount}>{formatCurrency(availableBalance)}</div>
          </div>

          <div style={styles.form}>
        <label style={styles.label}>Amount (KES)</label>
        <input
          type="number"
          style={styles.amountInput}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
        />

        <label style={styles.label}>Withdrawal Method</label>
        <div style={styles.methodSelector}>
          {withdrawMethods.map((method) => (
            <button
              key={method.id}
              style={{
                ...styles.methodButton,
                ...(withdrawMethod === method.id ? styles.activeMethod : {}),
              }}
              onClick={() => setWithdrawMethod(method.id)}
            >
              <span style={{ marginRight: spacing.sm }}>{method.icon}</span>
              {method.name}
            </button>
          ))}
        </div>

        <label style={styles.label}>
          {withdrawMethod === 'mpesa' ? 'M-Pesa Number' : 'Account Number'}
        </label>
        <input
          type={withdrawMethod === 'mpesa' ? 'tel' : 'text'}
          style={styles.input}
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          placeholder={
            withdrawMethods.find(m => m.id === withdrawMethod)?.placeholder || ''
          }
        />

        <button
          style={{
            ...styles.withdrawButton,
            opacity: loading ? 0.6 : 1,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
          onClick={handleWithdraw}
          disabled={loading}
        >
          {loading && <span style={styles.loadingIcon}>🔄</span>}
          {loading ? 'Processing...' : `Withdraw ${formatCurrency(parseFloat(amount) || 0)}`}
        </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Withdraw;